import { useEffect, useMemo, useRef, useState } from 'react';
import { JobProgress, Word, useWords } from '../../hooks/useWords';
import { getApiErrorMessage } from '../../services/api';
import { WordRow } from './WordRow';
import { BulkActions } from './BulkActions';
import { Plus, Loader2, ChevronLeft, ChevronRight, AlertCircle } from 'lucide-react';
import { ConfirmDialog } from '../shared/ConfirmDialog';

interface WordTableProps {
  words: Word[];
  topicId: number;
  onChange: () => void;
}

const PAGE_SIZES = [25, 50, 100];

type GenerationType = 'translation' | 'audio' | 'image' | 'all';

interface StartedJob {
  jobId: string;
  status: string;
  totalWords: number;
}

export function WordTable({ words, topicId, onChange }: WordTableProps) {
  const { addWord, updateWord, deleteWord, generateTranslations, generateAudio, generateImages, generateAll, pollJob } = useWords(topicId);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [newWord, setNewWord] = useState({ turkish: '', russian: '' });
  const [isAdding, setIsAdding] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);
  const [progress, setProgress] = useState<JobProgress | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZES[0]);

  const totalPages = Math.max(1, Math.ceil(words.length / pageSize));
  const pageOffset = (page - 1) * pageSize;
  const pageWords = useMemo(
    () => words.slice(pageOffset, pageOffset + pageSize),
    [words, pageOffset, pageSize],
  );

  // Keep the current page valid when words are added or removed.
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const selectedOnPage = pageWords.filter((w) => selectedIds.has(w.id)).length;
  const allPageSelected = pageWords.length > 0 && selectedOnPage === pageWords.length;
  const allSelected = words.length > 0 && selectedIds.size >= words.length;
  const selectAllRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = selectedOnPage > 0 && !allPageSelected;
    }
  }, [selectedOnPage, allPageSelected]);

  const toggleSelect = (id: number, checked: boolean) => {
    const next = new Set(selectedIds);
    if (checked) next.add(id);
    else next.delete(id);
    setSelectedIds(next);
  };

  // The header checkbox only covers the visible page; the bulk bar offers an explicit
  // "Выбрать все" for every word in the topic.
  const togglePage = (checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      for (const word of pageWords) {
        if (checked) next.add(word.id);
        else next.delete(word.id);
      }
      return next;
    });
  };

  const selectAllWords = () => setSelectedIds(new Set(words.map((w) => w.id)));
  const clearSelection = () => setSelectedIds(new Set());

  const setError = (err: unknown, fallback: string) => setActionError(getApiErrorMessage(err, fallback));

  const handleAdd = async () => {
    if (!newWord.turkish.trim() || isAdding) return;
    setIsAdding(true);
    setActionError(null);
    try {
      await addWord({ turkish: newWord.turkish.trim(), russian: newWord.russian.trim() || undefined });
      setNewWord({ turkish: '', russian: '' });
      onChange();
    } catch (err: unknown) {
      setError(err, 'Не удалось добавить слово');
    } finally {
      setIsAdding(false);
    }
  };

  const handleUpdate = async (id: number, data: Partial<Word>) => {
    // Rethrow: the row shows the failure inline next to the inputs, keeping the typed
    // values in edit mode.
    await updateWord(id, data);
    onChange();
  };

  const handleDelete = async (id: number) => {
    setActionError(null);
    try {
      await deleteWord(id);
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      onChange();
    } catch (err: unknown) {
      setError(err, 'Не удалось удалить слово');
    }
  };

  const handleBulkDelete = async () => {
    setActionError(null);
    try {
      for (const id of selectedIds) {
        await deleteWord(id);
      }
      clearSelection();
      onChange();
    } catch (err: unknown) {
      setError(err, 'Не удалось удалить выбранные слова');
    }
  };

  const startGeneration = async (type: GenerationType, wordIds?: number[]) => {
    setIsGenerating(true);
    setActionError(null);
    setProgress(null);
    try {
      const job: StartedJob = await (type === 'translation'
        ? generateTranslations(wordIds)
        : type === 'audio'
          ? generateAudio(wordIds)
          : type === 'image'
            ? generateImages(wordIds)
            : generateAll(wordIds));

      const result = await pollJob(job.jobId, { onProgress: setProgress });
      onChange();
      if (result.status === 'failed' || result.phaseProgress.failed > 0) {
        setActionError(result.errors[0] || 'Генерация завершилась с ошибками');
      }
    } catch (err: unknown) {
      setError(err, 'Ошибка генерации');
    } finally {
      setIsGenerating(false);
      setProgress(null);
    }
  };

  const runGeneration = (type: GenerationType) => {
    // No selection means "the whole topic" — the bulk bar says so.
    const ids = selectedIds.size > 0 ? Array.from(selectedIds) : undefined;
    void startGeneration(type, ids).finally(clearSelection);
  };

  const handleRowGenerate = (word: Word, type: GenerationType) => {
    void startGeneration(type, [word.id]);
  };

  return (
    <div className="space-y-3">
      <BulkActions
        selectedCount={selectedIds.size}
        totalCount={words.length}
        onSelectAll={selectAllWords}
        onClearSelection={clearSelection}
        onGenerateTranslations={() => runGeneration('translation')}
        onGenerateAudio={() => runGeneration('audio')}
        onGenerateImages={() => runGeneration('image')}
        onGenerateAll={() => runGeneration('all')}
        onDelete={() => setConfirmBulkDelete(true)}
        isGenerating={isGenerating}
        progress={progress}
      />

      <ConfirmDialog
        isOpen={confirmBulkDelete}
        title="Удалить выбранные слова?"
        message={`Будет удалено слов: ${selectedIds.size}. Действие необратимо.`}
        onCancel={() => setConfirmBulkDelete(false)}
        onConfirm={() => {
          setConfirmBulkDelete(false);
          void handleBulkDelete();
        }}
      />

      {actionError && (
        <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">
          <AlertCircle size={16} className="mt-0.5 shrink-0" />
          <span>{actionError}</span>
        </div>
      )}

      <div className="overflow-x-auto rounded-xl border border-gray-100 bg-white shadow-sm">
        <table className="w-full min-w-[900px] text-left text-sm">
          <thead className="bg-gray-50 text-gray-600">
            <tr>
              <th className="px-4 py-3">
                <input
                  ref={selectAllRef}
                  type="checkbox"
                  checked={allPageSelected}
                  onChange={(e) => togglePage(e.target.checked)}
                  title={allSelected ? 'Снять выделение с текущей страницы' : 'Выделить слова на текущей странице'}
                  aria-label="Выделить слова на текущей странице"
                  className="rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                />
              </th>
              <th className="px-4 py-3 font-medium">#</th>
              <th className="px-4 py-3 font-medium">Турецкий</th>
              <th className="px-4 py-3 font-medium">Русский</th>
              <th className="px-4 py-3 font-medium">Транскрипция</th>
              <th className="px-4 py-3 font-medium">Аудио</th>
              <th className="px-4 py-3 font-medium">Изображение</th>
              <th className="px-4 py-3 font-medium">Перевод</th>
              <th className="px-4 py-3 font-medium"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {pageWords.map((word, idx) => (
              <WordRow
                key={word.id}
                word={word}
                index={pageOffset + idx}
                onUpdate={handleUpdate}
                onDelete={(id) => void handleDelete(id)}
                onGenerate={handleRowGenerate}
                isSelected={selectedIds.has(word.id)}
                onSelect={toggleSelect}
              />
            ))}
            {pageWords.length === 0 && (
              <tr className="bg-gray-50/50">
                <td className="px-4 py-3 text-center text-gray-400" colSpan={9}>
                  Слов пока нет
                </td>
              </tr>
            )}
            <tr className="bg-gray-50/50">
              <td className="px-4 py-2"></td>
              <td className="px-4 py-2 text-gray-400">{words.length + 1}</td>
              <td className="px-4 py-2">
                <input
                  value={newWord.turkish}
                  onChange={(e) => setNewWord({ ...newWord, turkish: e.target.value })}
                  placeholder="Новое слово..."
                  className="w-full rounded border border-gray-300 px-2 py-1 text-sm"
                  onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
                />
              </td>
              <td className="px-4 py-2">
                <input
                  value={newWord.russian}
                  onChange={(e) => setNewWord({ ...newWord, russian: e.target.value })}
                  placeholder="Перевод"
                  className="w-full rounded border border-gray-300 px-2 py-1 text-sm"
                  onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
                />
              </td>
              <td className="px-4 py-2" colSpan={5}>
                <button
                  onClick={() => void handleAdd()}
                  disabled={isAdding || !newWord.turkish.trim()}
                  className="flex items-center gap-1 rounded-lg bg-brand-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-brand-700 disabled:opacity-50"
                >
                  {isAdding ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />}
                  Добавить
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2 text-sm text-gray-600">
        <span className="text-xs text-gray-500">
          {words.length === 0
            ? 'Нет слов'
            : `Показано ${pageOffset + 1}–${Math.min(pageOffset + pageWords.length, words.length)} из ${words.length}`}
        </span>

        <div className="flex items-center gap-3">
          <label className="flex items-center gap-1 text-xs text-gray-500">
            На странице
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(1);
              }}
              className="rounded border border-gray-300 bg-white px-2 py-1 text-xs text-gray-700 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
            >
              {PAGE_SIZES.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </label>

          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              aria-label="Предыдущая страница"
              className="rounded p-1 text-gray-500 hover:bg-gray-100 disabled:opacity-40 disabled:hover:bg-transparent"
            >
              <ChevronLeft size={16} />
            </button>
            <span className="px-1 text-xs">
              {page} / {totalPages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              aria-label="Следующая страница"
              className="rounded p-1 text-gray-500 hover:bg-gray-100 disabled:opacity-40 disabled:hover:bg-transparent"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}